import React, { useEffect, useState } from 'react';
import { getPaymentHistory } from '../../services/payment';
import { Payment } from '../../types/payment';

interface PaymentHistoryProps {
  userId?: string;
  title?: string;
  subtitle?: string;
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  userId,
  title = 'Recent payments',
  subtitle,
}) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        const paymentData = await getPaymentHistory(userId);
        setPayments(paymentData);
        setError(null);
      } catch {
        setError('Failed to fetch payment history');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentHistory();

    if (typeof window === 'undefined') return;

    const handleRefresh = () => {
      setLoading(true);
      fetchPaymentHistory();
    };

    window.addEventListener('tellurvision-payment-success', handleRefresh);

    return () => {
      window.removeEventListener('tellurvision-payment-success', handleRefresh);
    };
  }, [userId]);

  const content = (() => {
    if (loading) {
      return <p className="text-xs text-slate-500 dark:text-slate-400">Loading payment history…</p>;
    }

    if (error) {
      return <p className="text-xs text-red-600">{error}</p>;
    }

    if (payments.length === 0) {
      return <p className="text-xs text-slate-500 dark:text-slate-400">No payments recorded yet.</p>;
    }

    return (
      <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
        {payments.map((payment) => {
          const amount = typeof payment.amount === 'number' ? payment.amount : null;
          const currency = payment.currency || 'USD';
          const createdLabel = payment.createdAt
            ? new Date(payment.createdAt).toLocaleDateString()
            : 'Date N/A';
          const status = payment.status ?? 'unknown';
          const packageLabel = payment.campaignPackageName || payment.campaignPackageId || null;
          const adLabel = payment.campaignAdId || null;

          const statusClasses =
            status === 'completed'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : status === 'pending'
                ? 'bg-amber-50 text-amber-700 border-amber-100'
                : 'bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700';

          return (
            <li
              key={payment.id}
              className="flex items-center justify-between rounded-md bg-slate-50 dark:bg-slate-900 px-2 py-1"
            >
              <div>
                <div className="font-medium">
                  {amount !== null ? `${currency} ${amount.toFixed(2)}` : 'Amount N/A'}
                </div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400">{createdLabel}</div>
                {(packageLabel || adLabel) && (
                  <div className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400">
                    {packageLabel ? `Package: ${packageLabel}` : null}
                    {packageLabel && adLabel ? ' · ' : null}
                    {adLabel ? `Ad: ${adLabel}` : null}
                  </div>
                )}
              </div>
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusClasses}`}
              >
                {status}
              </span>
            </li>
          );
        })}
      </ul>
    );
  })();

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        {subtitle ? <p className="text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</p> : null}
      </div>
      {content}
    </div>
  );
};

export default PaymentHistory;
